import { MapPin, Salad, Bike, Sparkles } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { LeadCapture } from "@/components/LeadCapture";
import { DropSection } from "@/components/DropSection";
import { GiftBoxSection } from "@/components/GiftBoxSection";
import { IMAGES } from "@/data/site";
import { SEO } from "@/components/Seo";

const ZONES = [
  { icon: MapPin, t: "Delhi NCR", d: "Gurugram, South Delhi, Noida & Central Delhi in the first wave." },
  { icon: MapPin, t: "Bengaluru", d: "Indiranagar, Koramangala, HSR & Whitefield in the first wave." },
];

const PROMISES = [
  { icon: Salad, t: "Built like the guides", d: "Every bowl follows the same science you read here — protein anchored, fibre forward." },
  { icon: Sparkles, t: "Clean label, always", d: "No refined oil floods, no mystery sauces, no preservatives with chemistry degrees." },
  { icon: Bike, t: "Subscription or à la carte", d: "Daily lunch plans for workdays, single bowls for experiments." },
];

export default function ComingSoon() {
  return (
    <div data-testid="coming-soon-page">
      <SEO
        title="Coming Soon — Fresh Salads & Festive Drops in Delhi NCR & Bengaluru | Pranivaa Kitchens"
        description="Fresh salads, clean thalis and limited-edition festive drops are coming to Delhi NCR and Bengaluru. Join the city-tagged waitlist for early access."
        path="/coming-soon"
      />
      <section className="pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-5">
                Coming Soon
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-ink font-semibold mb-6">
                Fresh salads & clean thalis,{" "}
                <em className="text-transparent bg-clip-text bg-gradient-to-r from-leaf to-turmeric-deep">
                  delivered
                </em>
                .
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-ink-soft max-w-xl mb-8">
                Phase one was the science. Phase two is lunch. Pranivaa kitchens open soon in Delhi NCR
                and Bengaluru — and the waitlist eats first.
              </p>
              <div className="flex flex-wrap gap-4">
                {ZONES.map((z) => (
                  <div key={z.t} className="flex items-start gap-3 bg-paper-card border border-[#E7E2D7] rounded-2xl px-5 py-4">
                    <z.icon size={18} className="text-clay mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-sm text-ink">{z.t}</p>
                      <p className="text-xs text-ink-muted mt-0.5 max-w-[220px]">{z.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="overflow-hidden rounded-3xl shadow-lg mt-10">
              <img loading="lazy" decoding="async"
                src={IMAGES.freshSalad}
                alt="Vibrant plant-forward salad bowl with avocado, microgreens and seeds"
                className="w-full h-64 object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <div className="overflow-hidden rounded-3xl shadow-lg">
              <img loading="lazy" decoding="async"
                src={IMAGES.saladGrid}
                alt="Assorted fresh salad bowls prepared for city delivery"
                className="w-full h-64 object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <DropSection />

      <GiftBoxSection />

      <section className="py-16 md:py-24 bg-paper-muted/60 border-y border-[#E7E2D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold">
              What lands in your box.
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROMISES.map((p) => (
              <StaggerItem key={p.t} className="bg-paper-card border border-[#E7E2D7] rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <span className="w-11 h-11 rounded-full bg-turmeric-light text-turmeric-deep flex items-center justify-center mb-5">
                  <p.icon size={19} />
                </span>
                <h3 className="font-serif text-xl font-semibold text-ink mb-2">{p.t}</h3>
                <p className="text-sm leading-relaxed text-ink-soft">{p.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 md:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold mb-4">
              Be first in line for your city.
            </h2>
            <p className="text-base text-ink-soft">
              City-tagged, spam-free, and genuinely useful — the weekly science letter comes with it.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="bg-paper-card border border-[#E7E2D7] rounded-3xl p-7 md:p-10 shadow-lg">
            <LeadCapture source="coming-soon" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
